
import React, { useState, useEffect } from 'react';
import type { GenerationJob } from '../types';
import toast from 'react-hot-toast';

interface GenerationJobCardProps {
  job: GenerationJob;
  onDeleteJob: (jobId: string) => Promise<void>;
  onCancelJob: (jobId: string) => Promise<void>;
  onRetryJob: (job: GenerationJob) => void;
}

const GenerationJobCard: React.FC<GenerationJobCardProps> = ({ job, onDeleteJob, onCancelJob, onRetryJob }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Estimation Logic
  const isImportJob = job.jobType === 'import';
  const isExplanationJob = job.jobType === 'explanation';
  const isFixJob = job.jobType === 'fix';
  const isGenerationJob = job.jobType === 'generation';
  const isCreative = isGenerationJob && (job.modificationLevel === 4 || !job.baseProblemId);

  let estimatedDuration = 30;
  if (isImportJob) estimatedDuration = 60;
  else if (isExplanationJob) estimatedDuration = job.generationModel === 'gemini-2.5-pro' ? 40 : 20;
  else if (isFixJob) estimatedDuration = 10;
  else if (isGenerationJob) estimatedDuration = job.useThinkingMode ? 60 : (job.generationModel === 'gemini-2.5-pro' ? 30 : 15);

  // Progress Logic
  const [simulatedProgress, setSimulatedProgress] = useState(0);

  useEffect(() => {
      if (job.status === 'processing' && !isImportJob) {
          const interval = setInterval(() => {
              const elapsed = (Date.now() - new Date(job.createdAt).getTime()) / 1000;
              const percent = Math.min(95, (elapsed / estimatedDuration) * 100);
              setSimulatedProgress(percent);
          }, 1000);
          return () => clearInterval(interval);
      }
  }, [job.status, job.createdAt, estimatedDuration, isImportJob]);

  const currentProgress = isImportJob && job.progress ? job.progress.current : simulatedProgress;

  const handleAction = async (action: () => Promise<void>) => {
      setIsProcessing(true);
      try {
        await action();
      } catch (error) {
        toast.error('작업 실패');
      } finally {
        setIsProcessing(false);
      }
  };

  // Visual Config based on Job Type
  let accentColorClass = 'bg-gray-500';
  let typeLabel = 'Unknown';
  let title = '작업';
  let tags: { text: string, color: string }[] = [];

  if (isImportJob) {
      accentColorClass = 'bg-indigo-500';
      typeLabel = 'IMPORT';
      title = job.folder || '가져오기 작업';
      if(job.fileNames) tags.push({ text: `${job.fileNames.length} files`, color: 'bg-indigo-900/30 text-indigo-300' });
      if(job.importSource === 'dual') tags.push({ text: 'Dual', color: 'bg-blue-900/30 text-blue-300' });
  } else if (isGenerationJob) {
      accentColorClass = 'bg-teal-500';
      typeLabel = isCreative ? 'CREATION' : 'VARIATION';
      title = isCreative ? `창작: ${job.curriculumPath?.split('.').pop()}` : `변형: ID ${job.baseProblemId?.slice(0,6)}...`;
      if(job.generationModel?.includes('pro')) tags.push({ text: 'Pro', color: 'bg-purple-900/30 text-purple-300' });
      else tags.push({ text: 'Flash', color: 'bg-blue-900/30 text-blue-300' });
      if(job.modificationLevel) tags.push({ text: `Lv.${job.modificationLevel}`, color: 'bg-teal-900/30 text-teal-300' });
  } else if (isExplanationJob) {
      accentColorClass = 'bg-purple-500';
      typeLabel = 'EXPLANATION';
      title = `해설: ${job.targetProblemId?.slice(0,8)}`;
      tags.push({ text: job.generationModel?.includes('pro') ? '다정해설' : '빠른해설', color: 'bg-purple-900/30 text-purple-300' });
  } else if (isFixJob) {
      accentColorClass = 'bg-amber-500';
      typeLabel = 'FIX';
      title = `교정: ${job.targetProblemId?.slice(0,8)}`;
      tags.push({ text: job.fixTarget === 'problem' ? '문제' : '해설', color: 'bg-amber-900/30 text-amber-300' });
  }

  const isFailed = job.status === 'failed';

  return (
    <div className={`relative bg-[#27272A] border ${isFailed ? 'border-red-900/50' : 'border-[#3F3F46]'} rounded p-3 shadow-sm hover:shadow-md transition-all duration-200 group hover:border-[#52525B] select-none`}>
        {/* Left Accent Strip */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l ${isFailed ? 'bg-red-500' : accentColorClass}`}></div>
        
        <div className="pl-2 flex flex-col h-full justify-between">
            <div>
                <div className="flex justify-between items-center mb-1.5">
                    <span className={`text-[10px] font-bold tracking-wider ${isFailed ? 'text-red-400' : 'text-[#71717A]'}`}>{typeLabel}</span>
                    {isFailed && <span className="text-[10px] font-bold text-red-500 bg-red-900/20 px-1.5 rounded">FAILED</span>}
                </div>
                
                <h4 className="text-sm font-medium text-[#F4F4F5] mb-2 truncate leading-tight" title={title}>{title}</h4>
                
                {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                        {tags.map((t, i) => (
                            <span key={i} className={`text-[10px] font-medium px-1.5 py-0.5 rounded border border-white/5 ${t.color}`}>
                                {t.text}
                            </span>
                        ))}
                    </div>
                )}

                {job.status === 'processing' && (
                    <div className="mb-3">
                        <div className="flex justify-between text-[10px] text-[#A1A1AA] mb-1">
                            <span>{job.progress?.stage || '처리 중...'}</span>
                            <span>{Math.round(currentProgress)}%</span>
                        </div>
                        <div className="w-full bg-[#18181B] rounded-full h-1">
                            <div 
                                className={`h-1 rounded-full transition-all duration-500 ${accentColorClass}`} 
                                style={{ width: `${currentProgress}%` }}
                            ></div>
                        </div>
                    </div>
                )}
                
                {isFailed && job.error && (
                    <p className="text-[10px] text-red-400 bg-red-900/10 p-1 rounded mb-2 border border-red-900/20 line-clamp-2">
                        {job.error}
                    </p>
                )}
            </div>

            <div className="flex justify-between items-end pt-2 border-t border-[#3F3F46] mt-auto">
                <span className="text-[10px] text-[#52525B] font-mono">
                    {new Date(job.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {isFailed && isGenerationJob && (
                        <button onClick={() => onRetryJob(job)} disabled={isProcessing} className="p-1 hover:bg-[#3F3F46] rounded text-yellow-500" title="재시도">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                            </svg>
                        </button>
                    )}
                    <button 
                        onClick={() => handleAction(() => job.status === 'processing' || job.status === 'pending' ? onCancelJob(job.id) : onDeleteJob(job.id))} 
                        disabled={isProcessing}
                        className="p-1 hover:bg-[#3F3F46] rounded text-[#71717A] hover:text-red-400 transition-colors"
                        title={job.status === 'processing' ? '취소' : '삭제'}
                    >
                        {job.status === 'processing' || job.status === 'pending' ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </div>
    </div>
  );
};

export default React.memo(GenerationJobCard);
